// Wrong-queue lookup shared by the three put-away screens (goods-in Put
// Away, Shelf Drop put-away, Forecast Drop put-away).
//
// When a screen rejects a tote scan, it asks /put-away/tote/whereabouts
// which queue the tote actually belongs to and, if it is another put-away
// queue, offers to take the user there (2026-09-09: Forecast totes scanned
// in goods-in Put Away). The server reports facts per module; this module
// turns them into "the one queue to offer", excluding the screen the user
// is already on.
import { apiFetch } from "../../api/apiFetch";

export type QueueKey = "shelf" | "forecast" | "put_away" | "picking";

export interface DropQueueInfo {
    lines: number;
    units: number;
    pickedBy: string | null;
}

export interface ToteWhereabouts {
    valid: boolean;
    reason?: string;
    toteId?: string;
    toteStatus?: string;
    shelf?: DropQueueInfo | null;
    forecast?: DropQueueInfo | null;
    onWave?: boolean;
    goodsIn?: boolean;
}

export interface WrongQueueHome {
    queue: QueueKey;
    info: DropQueueInfo | null;
}

export const fetchToteWhereabouts = async (toteId: string): Promise<ToteWhereabouts | null> => {
    try {
        const res = await apiFetch("/put-away/tote/whereabouts", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ toteId }),
        });
        if (!res.ok) return null;
        const data = await res.json();
        return data && typeof data === "object" ? (data as ToteWhereabouts) : null;
    } catch {
        return null;
    }
};

// The queue to offer, in priority order: Shelf Drop, Forecast Drop, a
// picking wave (explanation only — its home is sorting/dispatch), then a
// goods-in book-in. Only a 'closed' tote is awaiting put-away anywhere;
// anything else (open session, stored) is left to the screen's existing
// status toasts.
export const homeQueueOf = (
    w: ToteWhereabouts | null,
    current: QueueKey,
): WrongQueueHome | null => {
    if (!w || !w.valid || w.toteStatus !== "closed") return null;
    const candidates: WrongQueueHome[] = [];
    if (w.shelf) candidates.push({ queue: "shelf", info: w.shelf });
    if (w.forecast) candidates.push({ queue: "forecast", info: w.forecast });
    if (w.onWave) candidates.push({ queue: "picking", info: null });
    if (w.goodsIn) candidates.push({ queue: "put_away", info: null });
    return candidates.find(c => c.queue !== current) ?? null;
};

// Route for "Take me there". Drop pages open straight into their put-away
// view via ?view=put-away; a picking tote has no put-away home.
export const queuePath = (queue: QueueKey): string | null => {
    switch (queue) {
        case "shelf": return "/pick-face/shelf-drop?view=put-away";
        case "forecast": return "/pick-face/forecast-drop?view=put-away";
        case "put_away": return "/put-away";
        default: return null;
    }
};

// One call for the screens: lookup + decide. Null means "no better home
// to offer" and the screen falls back to its usual rejection toast.
export const lookupWrongQueue = async (
    toteId: string,
    current: QueueKey,
): Promise<WrongQueueHome | null> => homeQueueOf(await fetchToteWhereabouts(toteId), current);
